/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon, EyeIcon, XMarkIcon } from '@heroicons/react/24/outline';

import MessageCard from '../../components/utils/MessageCard';
import DateRangePicker from '../../components/utils/DateRangePicker'; // Import the DateRangePicker

// Local imports
import GraphqlService from '../../service/graphqlService';

export default function UserActivity() {
  // State for DateRangePicker
  const [pickerStartDate, setPickerStartDate] = useState(null);
  const [pickerStartTime, setPickerStartTime] = useState('00:00');
  const [pickerEndDate, setPickerEndDate] = useState(null);
  const [pickerEndTime, setPickerEndTime] = useState('23:59');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [emailFilter, setEmailFilter] = useState('');
  const rowsPerPage = 10;

  // State for Action Details Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState('');

  // State to trigger search
  const [executeSearch, setExecuteSearch] = useState(0);

  // useEffect to set initial dates to last week as an example, or leave null
  useEffect(() => {
    const initialStart = moment().subtract(7, 'days').startOf('day');
    const initialEnd = moment().endOf('day');
    setPickerStartDate(initialStart);
    setPickerEndDate(initialEnd);
  }, []);

  const getActionsBetweenDates = async () => {
    if (!pickerStartDate || !pickerEndDate) return null; // Do not fetch if dates are not set

    const formattedStartDate = moment(pickerStartDate).set({
      hour: parseInt(pickerStartTime.split(':')[0], 10),
      minute: parseInt(pickerStartTime.split(':')[1], 10),
    }).format('YYYY-MM-DDTHH:mm:ssZ');

    const formattedEndDate = moment(pickerEndDate).set({
      hour: parseInt(pickerEndTime.split(':')[0], 10),
      minute: parseInt(pickerEndTime.split(':')[1], 10),
    }).format('YYYY-MM-DDTHH:mm:ssZ');

    const response = await GraphqlService.getActionsBetweenDates({
      from: formattedStartDate,
      to: formattedEndDate,
      page: currentPage,
      limit: rowsPerPage,
      email: emailFilter || undefined, // Pass undefined if emailFilter is empty
    });
    setCurrentPage(response.pagination?.pageNumber || 1);
    setTotalPages(response.pagination?.totalPages || 1);
    return response.data;
  };

  const {
    data,
    error: errorFetching,
    isLoading,
  } = useQuery([
    'UserActivity',
    currentPage,
    executeSearch, // Added executeSearch to query key
  ],
    () => getActionsBetweenDates(), {
    cacheTime: 0,
    // Enable query only when dates are selected and a search has been triggered
    enabled: executeSearch > 0 && !!pickerStartDate && !!pickerEndDate,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    refetchOnMount: false, // Explicitly prevent fetch on mount
  });

  const handleDateChange = (start, end) => {
    setPickerStartDate(start);
    setPickerEndDate(end);
    // Do not trigger search here, only on apply button
  };

  const handleApplyDateRange = () => {
    setCurrentPage(1); // Reset to first page on new date range
    if (pickerStartDate && pickerEndDate) {
      setExecuteSearch(prev => prev + 1); // Trigger search
    }
  };

  // Modal handler functions
  const openModal = (details) => {
    setModalContent(details);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalContent('');
  };

  const handleChangePage = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleEmailFilterChange = (event) => {
    setEmailFilter(event.target.value);
  };

  const handleEmailFilterSearch = () => {
    setCurrentPage(1); // Reset to first page on new filter
    if (pickerStartDate && pickerEndDate) {
      setExecuteSearch(prev => prev + 1); // Trigger search
    } else {
      // Optionally, inform the user to select a date range first
      // For example: alert('Please select a date range before filtering by email.');
      console.warn('Date range not selected for email filter search.');
    }
  };

  function renderContent() {
    if (errorFetching) {
      return <MessageCard message={`Error: ${errorFetching.message}`} type="error" />;
    }

    if (isLoading && executeSearch > 0) {
      return (
        <div className="flex flex-col items-center justify-center p-10 w-full">
          <svg className="animate-spin -ml-1 mr-3 h-10 w-10 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-lg text-gray-600 mt-4">Retrieving user activity, please wait...</p>
        </div>
      );
    }

    if (data && data.length > 0) {
      return (
        <div className="w-full overflow-x-auto">
          <div className="min-w-[900px] md:min-w-full inline-block align-middle">
            <table className="w-full divide-y divide-gray-200 table-fixed">
              <thead className="bg-gray-700">
                <tr>
                  {['Email', 'Timestamp', 'Entity Type', 'Action Type', 'Action Detail'].map((header) => (
                    <th key={header} scope="col" className={`px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider ${header === 'Action Detail' ? 'w-48' : ''}`}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((row) => (row && (
                  <tr key={row.id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 break-words max-w-xs">{row.cognitoEmail}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 break-words max-w-xs truncate">{moment(row.actionTimestamp).format('YYYY-MM-DD HH:mm:ss')}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 break-words max-w-xs truncate">{row.entityType}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 break-words max-w-xs truncate">{row.actionType}</td>
                    <td className="px-6 py-4 text-xs text-gray-500">
                      <div className="flex items-center space-x-2">
                        <span className="block truncate flex-grow" title={row.actionDetails}>
                          {row.actionDetails}
                        </span>
                        <button
                          onClick={() => openModal(row.actionDetails)}
                          className="text-blue-500 hover:text-blue-700 p-1"
                          title="View Details"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 bg-white rounded-b-lg">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handleChangePage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => handleChangePage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handleChangePage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                      {currentPage}
                    </span>
                    <button
                      onClick={() => handleChangePage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }
    if (!pickerStartDate || !pickerEndDate) {
      return <MessageCard message="Please select a date range to view user activity." type="info" />;
    }

    return <MessageCard message="No user activity found for the selected criteria." type="info" />;
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 bg-gray-100 min-h-screen">
      <div className="mb-6 p-6 bg-white rounded-xl shadow-lg">


        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <DateRangePicker
              selectedStartDate={pickerStartDate}
              setSelectedStartDate={setPickerStartDate}
              selectedStartTime={pickerStartTime}
              setSelectedStartTime={setPickerStartTime}
              selectedEndDate={pickerEndDate}
              setSelectedEndDate={setPickerEndDate}
              selectedEndTime={pickerEndTime}
              setSelectedEndTime={setPickerEndTime}
              onDateChange={handleDateChange} // This updates pickerStartDate/EndDate
              onApply={handleApplyDateRange} // This triggers the fetch
              datesOptional={false} // Make dates mandatory
              maxDaysBetweenDates={365} // Example: limit to one year
              setVisibility={true} // Prop from DateRangePicker, might not be needed if always visible
            />
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="emailFilter" className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Email
              </label>
              <div className="flex rounded-md shadow-sm">
                <input
                  type="email"
                  id="emailFilter"
                  name="emailFilter"
                  value={emailFilter}
                  onChange={handleEmailFilterChange}
                  placeholder="user@example.com"
                  className="focus:ring-blue-500 focus:border-blue-500 flex-1 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300 px-3 py-2"
                />
                <button
                  type="button"
                  onClick={handleEmailFilterSearch}
                  className="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  <span>Search</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {renderContent()}

      {/* Modal for Action Details */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="relative mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Action Details</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 whitespace-pre-wrap break-words text-left">
                  {modalContent}
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  id="ok-btn"
                  onClick={closeModal}
                  className="px-4 py-2 bg-blue-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
                >
                  Close
                </button>
              </div>
            </div>
            <button
              onClick={closeModal}
              className="absolute top-0 right-0 mt-4 mr-4 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
